/* HC CUSTOM: flyer viewer behaviour (sections/hc-flyer.liquid).
   - Dates: Shopify caches pages, so the server's start/end check can be stale. After the end date (data-end) the flyer is
     hidden and the visitor goes to data-fallback, or sees the "ended" message; the "starts on" note goes once the start
     date (data-start) has passed. Neither applies in the theme editor.
   - Views: the Flyer / Shop the flyer tabs switch data-view between "pages" and "list" on every screen size. #shop in
     the URL opens the list, and switching views keeps the hash in step so the back button and shared links work.
   - Product spots carry .js-quick-add, so quick-add.js opens Quick Buy from its document click handler; this only stops
     the link navigating. Modifier clicks (new tab or window) skip Quick Buy and open the product page.
   - In-store-only spots open a small popup beside the card (the item, the store address and a call button); clicking
     outside it, its close button or Escape closes it.
   - BSS B2B only prices cards it sees on a window scroll, so a scroll event goes out when the product grid appears.
   - The sticky tabs sit at --hcf-sticky-top, the sticky header's live bottom edge (trackHeader). */
if (!customElements.get('hc-flyer')) {
  class HcFlyer extends HTMLElement {
    connectedCallback() {
      this.designMode = this.hasAttribute('data-design-mode');
      if (this.checkDates()) return;

      this.tabs = [...this.querySelectorAll('[data-flyer-view]')];
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.setView(tab.dataset.flyerView, true));
      });
      this.addEventListener('click', this.handleClick.bind(this));
      window.addEventListener('hashchange', () => this.setView(this.viewFromHash(), false));

      this.storePop = this.querySelector('.hc-flyer__store-pop');
      if (this.storePop) {
        this.storePop.querySelector('.js-store-pop-close').addEventListener('click', () => this.closeStorePop(true));
        document.addEventListener('click', (evt) => {
          if (this.storeSpot && !this.storePop.contains(evt.target) && !evt.target.closest('.hc-flyer__spot--store')) {
            this.closeStorePop(false);
          }
        });
        document.addEventListener('keydown', (evt) => {
          if (evt.key === 'Escape' && this.storeSpot) this.closeStorePop(true);
        });
        window.addEventListener('resize', () => this.closeStorePop(false));
      }

      this.setView(this.viewFromHash(), false);
      this.trackHeader();
      this.classList.add('is-ready');
    }

    /**
     * Keeps the sticky tabs just under the sticky header. The theme's --header-height is measured once and the header
     * changes height once the page scrolls, so this follows the header's actual bottom edge instead.
     */
    trackHeader() {
      const header = document.querySelector('.shopify-section-group-header-group.cc-header')
        || document.querySelector('store-header');
      if (!header) return;

      const update = () => {
        this.headerFrame = null;
        const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
        if (bottom === this.headerBottom) return;
        this.headerBottom = bottom;
        this.style.setProperty('--hcf-sticky-top', `${bottom}px`);
      };
      const schedule = () => {
        if (!this.headerFrame) this.headerFrame = requestAnimationFrame(update);
      };
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      if ('ResizeObserver' in window) new ResizeObserver(schedule).observe(header);
      update();
    }

    /**
     * Repeats the server's date checks against the current time.
     * @returns {boolean} True when the sale has ended and the flyer is hidden.
     */
    checkDates() {
      if (this.designMode) return false;
      const now = Date.now();
      const end = Date.parse(this.dataset.end || '');
      if (end && now > end) {
        if (this.dataset.fallback) {
          window.location.replace(this.dataset.fallback);
        }
        this.dataset.state = 'ended';
        const ended = this.querySelector('.hc-flyer__ended');
        if (ended) ended.hidden = false;
        return true;
      }

      const start = Date.parse(this.dataset.start || '');
      if (start && now >= start) {
        const upcoming = this.querySelector('.js-flyer-upcoming');
        if (upcoming) upcoming.remove();
      }
      return false;
    }

    /**
     * @returns {string} The view named by the URL hash: "list" for #shop, otherwise "pages".
     */
    viewFromHash() {
      return window.location.hash === '#shop' ? 'list' : 'pages';
    }

    /**
     * Switches between the flyer and the product grid.
     * @param {string} view - "pages" or "list".
     * @param {boolean} fromTab - True when a tab was clicked (updates the hash and scrolls to the top of the view).
     */
    setView(view, fromTab) {
      if (view === this.dataset.view) return;
      this.dataset.view = view;
      this.tabs.forEach((tab) => {
        tab.setAttribute('aria-selected', String(tab.dataset.flyerView === view));
      });
      if (view === 'list') window.dispatchEvent(new Event('scroll'));
      if (!fromTab) return;

      const url = new URL(window.location.href);
      url.hash = view === 'list' ? 'shop' : '';
      window.history.replaceState(window.history.state, '', url);

      // Start the new view from its top when the tabs are stuck under the header.
      const body = this.querySelector('.hc-flyer__body');
      if (body && body.getBoundingClientRect().top < 0) {
        const tabs = this.querySelector('.hc-flyer__tabs');
        const offset = (tabs ? tabs.offsetHeight : 0)
          + parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height') || 0);
        window.scrollTo({ top: body.getBoundingClientRect().top + window.scrollY - offset });
      }
    }

    /**
     * Lets quick-add.js open Quick Buy for product spots without the link also navigating.
     * @param {MouseEvent} evt - Click event.
     */
    handleClick(evt) {
      const storeSpot = evt.target.closest('.hc-flyer__spot--store');
      if (storeSpot) {
        if (storeSpot === this.storeSpot) {
          this.closeStorePop(false);
        } else {
          this.openStorePop(storeSpot);
        }
        return;
      }

      const spot = evt.target.closest('.hc-flyer__spot.js-quick-add');
      if (!spot || !customElements.get('quick-add-drawer')) return;

      if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey || evt.button !== 0) {
        // Opening in a new tab or window: keep the browser's behaviour and skip Quick Buy.
        evt.stopPropagation();
        return;
      }
      evt.preventDefault();
    }

    /**
     * Shows the in-store-only popup next to a card: below it, or above it when there's no room below, inside the page.
     * @param {Element} spot - The in-store-only spot (a button).
     */
    openStorePop(spot) {
      this.closeStorePop(false);
      const page = spot.closest('.hc-flyer__page');
      if (!page || !this.storePop) return;

      this.storePop.querySelector('.js-store-pop-name').textContent = spot.dataset.title || '';
      page.appendChild(this.storePop);
      this.storePop.hidden = false;

      const pageRect = page.getBoundingClientRect();
      const spotRect = spot.getBoundingClientRect();
      const width = this.storePop.offsetWidth;
      const height = this.storePop.offsetHeight;
      let left = spotRect.left - pageRect.left + spotRect.width / 2 - width / 2;
      left = Math.max(8, Math.min(left, pageRect.width - width - 8));
      let top = spotRect.bottom - pageRect.top + 8;
      if (top + height > pageRect.height - 8) top = spotRect.top - pageRect.top - height - 8;
      this.storePop.style.left = `${left}px`;
      this.storePop.style.top = `${Math.max(8, top)}px`;

      this.storeSpot = spot;
      spot.setAttribute('aria-expanded', 'true');
      this.storePop.focus({ preventScroll: true });
    }

    /**
     * Hides the in-store-only popup.
     * @param {boolean} returnFocus - Move focus back to the card (keyboard close).
     */
    closeStorePop(returnFocus) {
      if (!this.storeSpot) return;
      this.storePop.hidden = true;
      this.storeSpot.setAttribute('aria-expanded', 'false');
      if (returnFocus) this.storeSpot.focus();
      this.storeSpot = null;
    }
  }

  customElements.define('hc-flyer', HcFlyer);
}
